<script>
import { mapGetters } from 'vuex'
import messages from '@/mixins/messages'
import lucra from '@/mixins/lucra'

export default {
    name: 'LauncherVerify',
    mixins: [messages, lucra],
    computed: {
        ...mapGetters(['userResource']),
        challengeId() {
            return this.$route.query.challenge
        },
        code() {
            return this.$route.query.code
        },
        seatName() {
            return this.challenge ? this.challenge.get('calendar_name') || this.challenge.get('requested_user_display') : ''
        },
        tournament() {
            return this.board ? this.board.cashTournament : null
        },
        alreadyJoined() {
            return !!(this.tournament && this.tournament.joined)
        },
        needsJoin() {
            return !!(this.tournament && this.tournament.joinable && !this.alreadyJoined)
        },
    },
    data() {
        return {
            loading: true,
            confirming: false,
            confirmed: false,
            joining: false,
            done: false,
            challenge: null,
            board: null,
        }
    },
    methods: {
        async load() {
            this.loading = true
            const LauncherChallengeResource = require('@/resources/launcherChallenge').default
            try {
                this.challenge = await LauncherChallengeResource.detail(this.challengeId)
                if (this.challenge.get('board')) {
                    const BoardResource = require('@/resources/board').default
                    this.board = await BoardResource.detail(this.challenge.get('board'))
                }
            } catch (e) {
                this.addMessage('danger', 'We could not load this request. The QR code may have expired, scan a fresh one at the simulator.')
            }
            this.loading = false
        },
        async confirm() {
            this.confirming = true
            try {
                await this.challenge.confirm(this.code)
                this.confirmed = true
                if (this.needsJoin) {
                    await this.runJoin()
                } else {
                    this.done = true
                }
            } catch (e) {
                this.addMessage('danger', this.getResponseMessageFromAxiosException(e))
            }
            this.confirming = false
        },
        async runJoin() {
            this.joining = true
            // Held back until the iframe is actually closed - it sits at z-index 3000 while
            // open, so a message (or the "done" screen) added while it's still up renders
            // underneath it, unseen.
            let pendingMessage = null
            let pendingDone = false
            try {
                await this.$lucraPrepareDeposit()
                await this.$lucraOpen(this.$refs.lucraContainer, { hidden: true })
                await this.$lucraLinkUser()
                let result = await this.$lucraJoinTournament(this.tournament.provider_matchup_id)
                if (result.needsDeposit) {
                    pendingMessage = ['info', 'Add funds to cover the buy-in, then select join again.']
                }
                if (result.locationError) {
                    pendingMessage = ['danger', 'We could not verify your location for a cash tournament. Try again with location services on.']
                } else if (result.joined) {
                    await this.board.joinCashTournament()
                    pendingDone = true
                }
            } catch (e) {
                pendingMessage = ['danger', this.getResponseMessageFromAxiosException(e)]
            }
            await this.$lucraCloseRoute()
            if (pendingMessage) {
                this.addMessage(...pendingMessage)
            }
            if (pendingDone) {
                this.done = true
            }
            this.joining = false
        },
    },
    mounted() {
        if (!this.challengeId || !this.code) {
            this.addMessage('danger', 'This link is missing its verification details.')
            this.loading = false
            return
        }
        this.load()
    },
}
</script>

<template>
    <div class="sa-page">
        <header-image kicker="Simulator-Anmeldung">
            <h1>Bestätige deine Identität</h1>
        </header-image>
        <div class="sa-page__inner">
            <messages />

            <b-skeleton-table :rows="4" v-if="loading" />

            <div class="sa-card lv-card" v-else-if="challenge">
                <template v-if="!confirmed">
                    <p class="lv-lead">Du wirst als Fahrer bei <strong>{{ seatName || 'einem Simulator' }}</strong> festgelegt.</p>
                    <div class="sa-note mb-3" v-if="tournament">
                        <div class="font-weight-bold text-uppercase">{{ tournament.title }}</div>
                        <div class="small text-muted">Geldturnier, ${{ (tournament.buy_in_amount_cents / 100).toFixed(2) }} Startgeld</div>
                    </div>
                    <b-button variant="primary" block :disabled="confirming" @click="confirm">
                        <b-spinner small v-if="confirming" /> Bestätigen, dass ich es bin
                    </b-button>
                </template>

                <template v-else>
                    <div v-if="joining" class="text-center py-3">
                        <b-spinner /> <div class="mt-2 text-muted">Deine Anmeldung zum Geldturnier wird abgeschlossen...</div>
                    </div>
                    <div v-else-if="done" class="text-center py-3">
                        <b-icon icon="check-circle-fill" font-scale="2" class="text-success" />
                        <p class="mt-3 mb-1 font-weight-bold">Alles erledigt.</p>
                        <p class="text-muted small">Kehre zum Simulator zurück, er ist für dich bereit.</p>
                    </div>
                </template>
            </div>

            <div class="sa-empty" v-else>Dieser Bestätigungslink ist nicht mehr gültig.</div>

            <div ref="lucraContainer" class="lv-lucra"></div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.lv-card {
    max-width: 460px;
    margin: 0 auto;
}
.lv-lead {
    font-size: 15px;
    line-height: 1.6;
}
.lv-lucra {
    position: fixed;
    inset: 0;
    z-index: 3000;
}
.lv-lucra:empty {
    display: none;
}
</style>
